import { useCallback, useMemo, useRef, useState } from 'react';

class Debounced<T> {
  private previousAction?: { id: number; action: () => Promise<T> };

  constructor(
    private readonly setState: (value: T) => void,
    private readonly setIsLoading: (value: boolean) => void,
    private readonly timeout = 300,
    private cancelled = false,
  ) {}

  debounce(action: () => Promise<T>, handleCancelledCooldownEvent?: () => void) {
    const id = Math.random();
    const newAction = { id, action };
    if (this.previousAction) {
      this.previousAction = undefined;
    }
    this.previousAction = newAction;
    this.cancelled = false;

    setTimeout(() => {
      // if there is no newer action added for execution, we can call the action
      if (this.previousAction?.id === id) {
        this.setIsLoading(true);
        newAction
          .action()
          .then((res: T) => {
            if (!this.cancelled) {
              this.handleActionResult(id, res);
            } else if (handleCancelledCooldownEvent) {
              handleCancelledCooldownEvent();
            }
          })
          .finally(() => this.setIsLoading(false));
      }
    }, this.timeout);
  }

  cancel() {
    this.cancelled = true;
  }

  private handleActionResult(id: number, res: T) {
    if (this.previousAction?.id !== id) {
      // this action has been cancelled, no need to set state
      return;
    }
    this.setState(res);
  }
}

/**
 * Debounce hook to handle repeated calls to an async function
 * This hook needs a parameter for setting the state and an optional timeout parameter
 *
 * Returns with a debounce function, a cancelDebounce function and a boolean indicating that the action is in progress
 *
 * Usage:
 * @example Simple example
 *   const [result, setResult] = useState<Array<string>>([]);
 *   const { debounce, cancelDebounce, isLoading } = useDebounce(setResult, 300);
 *
 *   const handleKeyPress = async () => {
 *     cancelDebounce(); // cancel previous search
 *     debounce(async () => {
 *       const res = await search();
 *       res.sort();
 *       return res;
 *     });
 *   }
 *
 * @example Advanced example
 * // If you want to handle the case when the action has been cancelled, and the modified state
 * // needed to be changed (or any other necessary action) you can pass a second parameter to the debounce function
 *  const handleKeyPress = async () => {
 *     setResult(['loading...']);
 *     cancelDebounce(); // cancel previous search
 *     debounce(async () => {
 *       const res = await search();
 *       res.sort();
 *       return res;
 *     }, () => {
 *       // this function runs when there were a debounced action.
 *       // The action is cancelled but the wrapped async call was already started, so this act as a cool down effect
 *       setResult(['cancelled']);
 *     });
 *   }
 *
 * @param setState
 * @param timeout
 */
export const useDebounce = <T>(setState: (val: T) => void, timeout: number = 300) => {
  const [isLoading, setIsLoading] = useState(false);
  const debouncer = useRef(new Debounced(setState, setIsLoading, timeout));
  return {
    debounce: useMemo(() => debouncer.current.debounce.bind(debouncer.current), [debouncer]),
    cancelDebounce: useCallback(() => debouncer.current.cancel(), [debouncer]),
    isLoading,
  };
};
